/**
 * `<FixAndResolvePanel/>` — *Fix and re-solve* (#1960, slice 2C of #1948).
 *
 * The founder (2 Oct 2026): "give an option for the deployer to suddenly
 * make a fixed allocation of an MC or something else because that person
 * cannot make it, and then they define for the solver to resolve around the
 * fixed allocations, while trying to make as minimal adjustments as
 * possible … the user needs to define which period they want to resolve
 * for, and … make sure that the fixed allocations are still retained."
 *
 * **Why the lead comes:** someone cannot make it and the ladder's one-seat
 * remedies are not enough — they want the dates re-solved around what is
 * fixed. **What they see first:** the absence already pinned off, the
 * day's free people ready to seat. **What they adjust straight away:** add a
 * seat or a pin, check the dates, Run.
 *
 * #2029 — the toolbar's standing door opens it on the dates on screen
 * (`request.dates`: step 2 starts on *Custom* with them), with each person
 * who still holds a seat there and is off that date pinned off (covered or
 * not; planned leave with no seat under it is not), or nothing pinned when
 * nobody qualifies.
 *
 * One panel in the right-panel overlay, three steps, no modal:
 *   1. **Fixed allocations** — the absence pinned `off`; *Seat <name> here*
 *      from the ladder's free list for each short cell; *Pin a cell* (member
 *      + date + station + session, or Off). Each pin is a chip with ×. Pins
 *      after the range stay greyed, and one line names them with **Stretch
 *      to N days** (#1997).
 *   2. **Dates to re-solve** — *N days* (default: the team's days a
 *      re-solve may reach, 5 unless set, from the first pinned date,
 *      clipped to the last published date) / *Week* / *Fortnight* /
 *      *Custom* (*Week* hidden when N is 7, *Fortnight* when it is 14), and
 *      beside them the reach (#1987): **Days** and **Shifts moved up to** (a
 *      third of the team unless set), prefilled from the team defaults.
 *      Under them **Also fill gaps that were already there** (off). One
 *      line: `N dates · M pins · up to S shifts moved`.
 *   3. **Run** — `POST /teams/{id}/roster/repair`; then the shared review
 *      screen (`<ChangeReviewPanel/>`, #1961) replaces the steps; Apply →
 *      `/repair/{id}/apply` with the review's decisions and the off pins as
 *      absences (recorded once, as the row "Report off sick" writes). With
 *      no draft the run says why in one line (#1997): *Can't seat the cover*
 *      (proven) with **Widen** — twice the shifts, run again — or *Ran out of
 *      time* with **Run again with more time** (twice the first run's time,
 *      which scales with the job's size since #2029) and,
 *      when seats are known, **Widen**. A draft that leaves seats short
 *      offers **Widen** beside its review line. The line clears as soon as
 *      the lead changes a pin, the dates, the limits or the old-gaps box.
 *
 * #1998 — the re-solve is a job on the solver worker. While it runs step 3
 * reads *Solving around N pins… S s*; a job still queued after 15 s adds
 * *Waiting for the solver to start…* (the on-demand worker may be waking).
 * A job that failed puts its sentence in the error line, with Run there
 * again. A change to any input stops the run in flight — and, since #2004,
 * its job on the worker too, as closing the panel (unmount) does.
 */
import { useQueries } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ApiError, apiFetch } from "../../api/client";
import { useCycleConfig } from "../../api/useCycleConfig";
import { useCycleRoster } from "../../api/useCycleRoster";
import {
  REPAIR_QUEUED_HINT_S,
  type RepairResponse,
  useApplyRepair,
  useProposeRepair,
  useRepairReach,
} from "../../api/useRosterRepair";
import {
  type ShortfallOptionsResponse,
  shortfallOptionsQueryKey,
  useShortfallOptions,
} from "../../api/useShortfallOptions";
import { useAuth } from "../../lib/AuthContext";
import { type ReviewChange, type ReviewDecision, reviewDate } from "../../lib/changeReview";
import { cn } from "../../lib/cn";
import {
  DEFAULT_DAYS_FORWARD,
  DEFAULT_SHIFTS_MOVED,
  type DateRange,
  type FlowPin,
  MAX_DAYS_FORWARD,
  type RangePreset,
  type Reach,
  absencePins,
  datesOf,
  dayCount,
  effectivePreset,
  firstPinDate,
  leftOutText,
  pinKey,
  pinsAfterRange,
  pinsInRange,
  presetLabel,
  presetRange,
  rangeDaysForward,
  stretchDays,
  visiblePresets,
  widenReach,
} from "../../lib/fixAndResolve";
import { lastPublishedDayIso } from "../../lib/publishedDays";
import type { FixAndResolveRequest } from "../../store/fixAndResolveRequest";
import { ChangeReviewPanel } from "../review/ChangeReviewPanel";
import { Button } from "../ui";

export interface FixAndResolvePanelProps {
  request: FixAndResolveRequest;
  /** After a successful apply, with the cells it changed. */
  onApplied: (changedCells: number) => void;
}

const FREE_SHOWN = 4;

function pinLabel(p: FlowPin): string {
  if (p.off) return `${p.display_name} off · ${reviewDate(p.date)}`;
  const sess = p.session && p.session !== "FULL_DAY" ? ` ${p.session}` : "";
  return `${p.display_name} · ${p.station ?? ""}${sess} · ${reviewDate(p.date)}`;
}

/** The sentence of a refused repair: the API's `message` when it sends one. */
export function repairErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { error?: { detail?: unknown } } | null)?.error?.detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string" && message) return message;
    }
    return err.detail;
  }
  return err instanceof Error ? err.message : "Not run.";
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The one line a run outside the reach shows beside **Widen** (#1987). */
export function reachExceededText(res: RepairResponse): string {
  const limit = `moving up to ${plural(res.max_shifts_moved, "shift")} in ${plural(
    res.max_days_forward,
    "day",
  )}`;
  // `shifts_needed` is what the same re-solve moves with no limit — an
  // upper bound on what seating the cover takes, so it is named as such.
  // #1997: the server sends it only when that solve finished.
  const needs =
    res.shifts_needed != null
      ? ` (with no limit it moves ${plural(res.shifts_needed, "shift")})`
      : "";
  return `Can't seat the cover ${limit}${needs}.`;
}

/**
 * The one line a run the solver stopped on time shows (#1997): the seats
 * its best answer left, or that it found none. When more time is no longer
 * on offer it points at the days instead.
 */
export function timedOutText(res: RepairResponse): string {
  const limit = `up to ${plural(res.max_shifts_moved, "shift")} in ${plural(
    res.max_days_forward,
    "day",
  )}`;
  const head =
    res.seats_left_short != null
      ? `Ran out of time with ${plural(res.seats_left_short, "seat")} still to cover (${limit}).`
      : `Ran out of time before it found an answer (${limit}).`;
  return res.more_time_available ? head : `${head} Try fewer days.`;
}

/** Whether **Widen** can help a run with no draft (#1997). */
function widenHelps(res: RepairResponse): boolean {
  // More shifts can seat a cover the reach held back, or the seats a
  // stopped run left short. With no answer at all the run is too big for
  // its time, and more shifts do not make it smaller.
  return res.status === "reach_exceeded" || res.seats_left_short != null;
}

/** What the last Run sent, so *Run again with more time* repeats it. */
interface Sent {
  range: DateRange;
  pins: FlowPin[];
  limits: Reach;
  moreTime: boolean;
  fillOldGaps: boolean;
}

/** Whole seconds since `startedAt`, ticking once a second while `active`. */
function useSecondsSince(startedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [startedAt]);
  return startedAt === null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000));
}

function cellMatches(
  cell: { station: string; session: string },
  change: { session: string; after_station: string | null },
): boolean {
  if (!change.after_station || cell.station !== change.after_station) return false;
  return cell.session === change.session || cell.session === "FULL_DAY";
}

export function FixAndResolvePanel({ request, onApplied }: FixAndResolvePanelProps) {
  const { teamId, cycleId, date } = request;
  const { accountId } = useAuth();
  const roster = useCycleRoster(cycleId, { show: "everyone" });
  const config = useCycleConfig(cycleId);
  const shortfalls = useShortfallOptions(cycleId, date);
  const propose = useProposeRepair(teamId);
  const apply = useApplyRepair(teamId);
  const reach = useRepairReach(teamId);

  const [pins, setPins] = useState<FlowPin[]>(() => [
    ...absencePins(request.absent),
    // #2715 - Offer the move starts with the suggested person on the seat.
    ...(request.seatPin ? [{ ...request.seatPin, off: false }] : []),
  ]);
  // #2029 — a door that names the dates (the toolbar's, on the dates on
  // screen) starts on Custom with them; the others on the team's days.
  const [preset, setPreset] = useState<RangePreset>(request.dates ? "custom" : "days");
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(request.dates ?? null);
  // #1987 — this repair's reach; null = the team default (prefilled).
  const [daysSet, setDaysSet] = useState<number | null>(null);
  const [shiftsSet, setShiftsSet] = useState<number | null>(null);
  // Founder 3 Oct 2026 — fill the gaps the roster already had too (off).
  const [fillOldGaps, setFillOldGaps] = useState(false);
  const [picking, setPicking] = useState(false);
  const [draft, setDraft] = useState<RepairResponse | null>(null);
  // A run with no draft (#1997): `reach_exceeded` or `timed_out`.
  const [verdict, setVerdict] = useState<RepairResponse | null>(null);
  const [lastSent, setLastSent] = useState<Sent | null>(null);
  const [runPins, setRunPins] = useState<FlowPin[]>([]);

  const names = useMemo(() => {
    const out = new Map<string, string>();
    for (const m of roster.data?.members ?? []) out.set(m.id, m.display_name);
    for (const a of request.absent) if (a.display_name) out.set(a.membership_id, a.display_name);
    return out;
  }, [roster.data, request.absent]);

  // The window's days and its last published date cap the presets.
  const rosterDays = useMemo(() => (roster.data?.days ?? []).map((d) => d.date), [roster.data]);
  const lastDay = useMemo(
    () =>
      lastPublishedDayIso(
        (roster.data?.assignments ?? []).map((a) => ({
          date: a.date,
          is_published: a.is_published,
        })),
      ) ??
      rosterDays[rosterDays.length - 1] ??
      null,
    [roster.data, rosterDays],
  );
  // The reach (#1987): the days the default preset runs and the shifts a
  // run may move, the team's defaults until the lead types over them.
  const days = daysSet ?? reach.data?.max_days_forward ?? DEFAULT_DAYS_FORWARD;
  const shifts = shiftsSet ?? reach.data?.max_shifts_moved ?? DEFAULT_SHIFTS_MOVED;
  // Every preset runs from the first pinned date (on or after the day). A
  // *Week* / *Fortnight* the days preset already covers is not shown.
  const presetNow = effectivePreset(preset, days);
  const anchor = firstPinDate(pins, date);
  const range = presetRange(presetNow, anchor, lastDay, custom ?? undefined, days);
  const nDates = dayCount(range);
  const sent = pinsInRange(pins, range);
  const daysForward = rangeDaysForward(presetNow, days, firstPinDate(sent, range.from), range);
  // #1997 — pins after the range are left out of the run: say so, and offer
  // the days that reach the last of them when that would take them in.
  const leftOut = pinsAfterRange(pins, range);
  const stretchTo = leftOut.last ? stretchDays(anchor, leftOut.last) : 0;
  const canStretch =
    stretchTo > 0 && presetRange("days", anchor, lastDay, undefined, stretchTo).to > range.to;

  // #1998 — the run's job: seconds since Run, and whether it still waits.
  const solvingFor = useSecondsSince(propose.progress?.startedAt ?? null);
  const stillQueued = propose.progress?.state === "queued" && solvingFor >= REPAIR_QUEUED_HINT_S;

  // An answer on screen is for the inputs it ran with: any change clears it
  // (and the red error line), and stops a run still in flight (#1998).
  const touched = () => {
    if (propose.isPending) propose.cancel();
    setVerdict(null);
    propose.reset();
  };
  const addPin = (p: FlowPin) => {
    touched();
    setPins((prev) => (prev.some((x) => pinKey(x) === pinKey(p)) ? prev : [...prev, p]));
  };
  const removePin = (p: FlowPin) => {
    touched();
    setPins((prev) => prev.filter((x) => pinKey(x) !== pinKey(p)));
  };

  const send = (s: Sent) => {
    setRunPins(s.pins);
    setVerdict(null);
    setDraft(null);
    setLastSent(s);
    propose.mutate(
      {
        from: s.range.from,
        to: s.range.to,
        pins: s.pins.map((p) =>
          p.off
            ? { membership_id: p.membership_id, date: p.date, off: true }
            : {
                membership_id: p.membership_id,
                date: p.date,
                off: false,
                station: p.station ?? null,
                session: p.session ?? null,
              },
        ),
        max_shifts_moved: s.limits.shifts,
        max_days_forward: s.limits.days,
        more_time: s.moreTime,
        fill_old_gaps: s.fillOldGaps,
      },
      {
        onSuccess: (res) => {
          if (res.status === "ok") setDraft(res);
          else setVerdict(res);
        },
      },
    );
  };
  const run = () =>
    send({
      range,
      pins: sent,
      limits: { shifts, days: daysForward },
      moreTime: false,
      fillOldGaps,
    });
  // *Run again with more time* (#1997): the same request, more time a window.
  const runLonger = () => {
    if (lastSent) send({ ...lastSent, moreTime: true });
  };
  // *Widen* (founder, 3 Oct 2026): one click doubles the shifts and runs the
  // same dates again — the lead's range and preset stay as they are. A run
  // already given more time keeps it.
  const wider = widenReach({ shifts, days: daysForward });
  const widen = () => {
    setShiftsSet(wider.shifts);
    send({
      range,
      pins: sent,
      limits: wider,
      moreTime: lastSent?.moreTime ?? false,
      fillOldGaps,
    });
  };
  const widenTitle = `Run again moving up to ${plural(wider.shifts, "shift")} in ${plural(
    wider.days,
    "day",
  )}`;

  // Alternatives for a filled seat: the ladder's free people for that cell
  // (rung 0 of `GET /shortfalls`), for each date of the draft in this window.
  const draftDates = useMemo(() => {
    const days = new Set(rosterDays);
    const out = new Set<string>();
    for (const c of draft?.changes ?? []) if (c.after_station && days.has(c.date)) out.add(c.date);
    return [...out].sort().slice(0, 14);
  }, [draft, rosterDays]);
  const ladders = useQueries({
    queries: draftDates.map((d) => ({
      queryKey: shortfallOptionsQueryKey(cycleId, d, accountId),
      enabled: Boolean(accountId),
      staleTime: 15_000,
      retry: false,
      queryFn: () =>
        apiFetch<ShortfallOptionsResponse>(
          `/api/cycles/${cycleId}/shortfalls?date=${encodeURIComponent(d)}`,
        ),
    })),
  });
  const ladderCells = ladders.flatMap((q) =>
    (q.data?.cells ?? []).map((cell) => ({ date: q.data?.date ?? "", cell })),
  );

  const reviewChanges: ReviewChange[] = (draft?.changes ?? []).map((c) => {
    const cells = ladderCells.filter((x) => x.date === c.date && cellMatches(x.cell, c));
    const alternatives = cells.flatMap((x) =>
      (x.cell.free ?? []).map((f) => ({
        membership_id: f.membership_id,
        display_name: f.display_name,
        reason: f.doing,
      })),
    );
    return {
      membership_id: c.membership_id,
      display_name: names.get(c.membership_id) ?? "Member",
      date: c.date,
      session: c.session,
      before_station: c.before_station,
      after_station: c.after_station,
      alternatives,
    };
  });

  const applyDecision = async (decision: ReviewDecision) => {
    if (!draft?.draft_id) return;
    const res = await apply.mutateAsync({
      draftId: draft.draft_id,
      cycleIds: [cycleId],
      body: {
        skip: decision.skip,
        seat: decision.seat,
        absences: runPins
          .filter((p) => p.off)
          .map((p) => ({ membership_id: p.membership_id, date: p.date })),
        // #2300 — Fix and re-solve writes as it always has (a published
        // window's rows go out); a day load *Move* and, since #2715, *Offer
        // the move* hold their draft until Publish.
        hold_for_publish: request.holdForPublish ?? false,
        ...(request.coverFor ? { cover_for_fixed_shift_id: request.coverFor } : {}),
      },
    });
    onApplied(res.changed_cells);
  };

  if (draft) {
    const short = draft.seats_left_short ?? 0;
    return (
      <div className="space-y-3 text-sm" data-testid="fix-resolve-review">
        {/* The line wraps inside its own box (12rem basis, grows to fill)
            so Widen keeps its place beside it; the row only breaks under
            the line when not even 12rem plus the button fit (4 Oct). */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p
            className="min-w-0 grow basis-48 text-xs text-fg-muted"
            data-testid="fix-resolve-review-line"
          >
            {reviewDate(draft.from)}
            {draft.to !== draft.from ? ` – ${reviewDate(draft.to)}` : ""} · {draft.changed_cells}{" "}
            changed cell{draft.changed_cells === 1 ? "" : "s"} ·{" "}
            {plural(draft.shifts_moved, "shift")} moved (up to {draft.max_shifts_moved})
            {short > 0 ? ` · ${plural(short, "seat")} still short` : ""}
          </p>
          {/* #1997 (decision E) — a draft that leaves seats short offers
              Widen: it drops this draft and runs again with more shifts. */}
          {short > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 shrink-0 px-2"
              data-testid="fix-resolve-review-widen"
              title={widenTitle}
              onClick={() => {
                apply.reset();
                widen();
              }}
            >
              Widen
            </Button>
          ) : null}
        </div>
        <ChangeReviewPanel
          changes={reviewChanges}
          pins={runPins.map((p) => ({
            membership_id: p.membership_id,
            date: p.date,
            off: p.off,
            station: p.station ?? null,
            session: p.session ?? null,
          }))}
          onApply={applyDecision}
          onDiscard={() => {
            setDraft(null);
            apply.reset();
          }}
          testid="fix-resolve-review-panel"
        />
      </div>
    );
  }

  const cells = (shortfalls.data?.cells ?? []).filter((c) => (c.free ?? []).length > 0);
  const stations = (config.data?.stations ?? []).filter((s) => s.enabled !== false);

  return (
    <div className="space-y-4 text-sm" data-testid="fix-resolve-panel">
      <section className="space-y-2" aria-labelledby="fix-resolve-step-1">
        <h3 id="fix-resolve-step-1" className="text-xs font-semibold text-fg">
          1 · Fixed allocations
        </h3>
        <ul className="flex flex-wrap gap-1.5" data-testid="fix-resolve-pins">
          {pins.map((p) => {
            const inRange = p.date >= range.from && p.date <= range.to;
            return (
              <li
                key={pinKey(p)}
                data-testid="fix-resolve-pin"
                className={cn(
                  "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px]",
                  p.off ? "border-border-strong text-fg" : "border-accent bg-accent-soft/30",
                  inRange ? "" : "opacity-50",
                )}
              >
                {pinLabel(p)}
                <button
                  type="button"
                  aria-label={`Remove ${pinLabel(p)}`}
                  className="text-fg-subtle hover:text-fg"
                  onClick={() => removePin(p)}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
        {leftOut.dates > 0 ? (
          <div
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-fg-muted"
            data-testid="fix-resolve-left-out"
          >
            <span>{leftOutText(leftOut)}</span>
            {canStretch ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2"
                data-testid="fix-resolve-stretch"
                title="Longer ranges take longer to solve"
                onClick={() => {
                  touched();
                  setDaysSet(Math.min(MAX_DAYS_FORWARD, stretchTo));
                  setPreset("days");
                }}
              >
                Stretch to {plural(stretchTo, "day")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {cells.length > 0 ? (
          <ul className="space-y-1.5" data-testid="fix-resolve-seat-cells">
            {cells.map((cell) => {
              const seated = pins.filter(
                (p) =>
                  !p.off &&
                  p.date === date &&
                  p.station === cell.station &&
                  p.session === cell.session,
              ).length;
              const open = cell.shortfall - seated;
              return (
                <li key={`${cell.station}|${cell.session}`} className="space-y-1">
                  <p className="text-[11px] text-fg-muted">
                    {cell.station} · {cell.session === "FULL_DAY" ? "Full day" : cell.session} ·{" "}
                    {reviewDate(date)}
                    {open > 0 ? ` · ${open} short` : ""}
                  </p>
                  {open > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(cell.free ?? [])
                        .filter(
                          (f) =>
                            !pins.some(
                              (p) => p.membership_id === f.membership_id && p.date === date,
                            ),
                        )
                        .slice(0, FREE_SHOWN)
                        .map((f) => (
                          <Button
                            key={f.membership_id}
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2"
                            data-testid={`fix-resolve-seat-${f.membership_id}`}
                            title={f.doing}
                            onClick={() =>
                              addPin({
                                membership_id: f.membership_id,
                                display_name: f.display_name,
                                date,
                                off: false,
                                station: cell.station,
                                session: cell.session === "ONCALL" ? null : cell.session,
                              })
                            }
                          >
                            Seat {f.display_name} here
                          </Button>
                        ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {picking ? (
          <PinPicker
            members={roster.data?.members ?? []}
            dates={datesOf(range)}
            stations={stations}
            onAdd={(p) => {
              addPin(p);
              setPicking(false);
            }}
            onCancel={() => setPicking(false)}
          />
        ) : (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            data-testid="fix-resolve-pin-cell"
            onClick={() => setPicking(true)}
          >
            Pin a cell
          </Button>
        )}
      </section>

      <section className="space-y-2" aria-labelledby="fix-resolve-step-2">
        <h3 id="fix-resolve-step-2" className="text-xs font-semibold text-fg">
          2 · Dates to re-solve
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <fieldset className="flex flex-wrap gap-1" aria-label="Dates to re-solve">
            {visiblePresets(days).map((p) => (
              <button
                key={p.key}
                type="button"
                aria-pressed={presetNow === p.key}
                data-testid={`fix-resolve-range-${p.key}`}
                onClick={() => {
                  touched();
                  if (p.key === "custom" && !custom) setCustom(range);
                  setPreset(p.key);
                }}
                className={cn(
                  "rounded-sm border px-2 py-0.5 text-[11px]",
                  presetNow === p.key
                    ? "border-accent bg-accent-soft/50 text-fg"
                    : "border-border text-fg-muted hover:text-fg",
                )}
              >
                {presetLabel(p.key, days)}
              </button>
            ))}
          </fieldset>
          {/* #1987 — the reach, prefilled from Setup → Rules & fairness. */}
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-fg-muted">
            <label className="flex items-center gap-1">
              Days
              <input
                type="number"
                min={1}
                max={62}
                inputMode="numeric"
                data-testid="fix-resolve-days"
                className="w-12 rounded-sm border border-border bg-bg px-1 py-0.5 text-fg"
                value={daysForward}
                onChange={(e) => {
                  const v = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(v)) return;
                  touched();
                  setDaysSet(Math.min(MAX_DAYS_FORWARD, Math.max(1, v)));
                  setPreset("days");
                }}
              />
            </label>
            <label className="flex items-center gap-1">
              Shifts moved up to
              <input
                type="number"
                min={0}
                max={500}
                inputMode="numeric"
                data-testid="fix-resolve-shifts"
                className="w-12 rounded-sm border border-border bg-bg px-1 py-0.5 text-fg"
                value={shifts}
                onChange={(e) => {
                  const v = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(v)) return;
                  touched();
                  setShiftsSet(Math.min(500, Math.max(0, v)));
                }}
              />
            </label>
          </div>
        </div>
        {presetNow === "custom" ? (
          <div className="flex items-center gap-2 text-[11px]">
            <input
              type="date"
              aria-label="From"
              data-testid="fix-resolve-from"
              className="rounded-sm border border-border bg-bg px-1.5 py-0.5"
              value={range.from}
              onChange={(e) => {
                touched();
                setCustom({ from: e.target.value, to: range.to });
              }}
            />
            <span className="text-fg-faint">to</span>
            <input
              type="date"
              aria-label="To"
              data-testid="fix-resolve-to"
              className="rounded-sm border border-border bg-bg px-1.5 py-0.5"
              value={range.to}
              onChange={(e) => {
                touched();
                setCustom({ from: range.from, to: e.target.value });
              }}
            />
          </div>
        ) : null}
        {/* Founder 3 Oct 2026 — the lead chooses per repair whether gaps the
            roster already had are filled too. */}
        <div className="space-y-0.5">
          <label className="flex items-start gap-1.5 text-[11px] text-fg">
            <input
              type="checkbox"
              className="mt-0.5"
              data-testid="fix-resolve-fill-old-gaps"
              checked={fillOldGaps}
              onChange={(e) => {
                touched();
                setFillOldGaps(e.target.checked);
              }}
            />
            Also fill gaps that were already there
          </label>
          <p className="text-[11px] text-fg-muted">
            Off: the re-solve only covers the absence. On: it may also fill seats that were short
            before, which moves more people.
          </p>
        </div>
        <p className="text-[11px] text-fg-muted" data-testid="fix-resolve-summary">
          {reviewDate(range.from)}
          {range.to !== range.from ? ` – ${reviewDate(range.to)}` : ""} · {nDates} date
          {nDates === 1 ? "" : "s"} · {sent.length} pin{sent.length === 1 ? "" : "s"} · up to{" "}
          {plural(shifts, "shift")} moved
        </p>
      </section>

      <section className="space-y-2" aria-labelledby="fix-resolve-step-3">
        <h3 id="fix-resolve-step-3" className="text-xs font-semibold text-fg">
          3 · Run
        </h3>
        {propose.isPending ? (
          <div className="space-y-0.5">
            <output
              className="flex items-center gap-1.5 text-xs text-fg-muted"
              data-testid="fix-resolve-running"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Solving around {plural(runPins.length, "pin")}… {solvingFor} s
            </output>
            {stillQueued ? (
              <p className="text-[11px] text-fg-muted" data-testid="fix-resolve-queued">
                Waiting for the solver to start…
              </p>
            ) : null}
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            data-testid="fix-resolve-run"
            disabled={nDates === 0 || nDates > 62}
            onClick={run}
          >
            Run
          </Button>
        )}
        {propose.isError ? (
          <p role="alert" className="text-xs text-danger" data-testid="fix-resolve-run-error">
            {repairErrorText(propose.error)}
          </p>
        ) : null}
        {verdict && !propose.isPending ? (
          <output
            className="flex flex-wrap items-center gap-2"
            data-testid={
              verdict.status === "timed_out"
                ? "fix-resolve-timed-out"
                : "fix-resolve-reach-exceeded"
            }
          >
            <span className="text-xs text-fg">
              {verdict.status === "timed_out" ? timedOutText(verdict) : reachExceededText(verdict)}
            </span>
            {verdict.status === "timed_out" && verdict.more_time_available ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2"
                data-testid="fix-resolve-more-time"
                title="The same run, with longer to find an answer"
                onClick={runLonger}
              >
                Run again with more time
              </Button>
            ) : null}
            {widenHelps(verdict) ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2"
                data-testid="fix-resolve-widen"
                title={widenTitle}
                onClick={widen}
              >
                Widen
              </Button>
            ) : null}
          </output>
        ) : null}
      </section>
    </div>
  );
}

interface PinPickerProps {
  members: readonly { id: string; display_name: string }[];
  dates: readonly string[];
  stations: readonly { name: string; sessions?: string[] | null; on_call: boolean }[];
  onAdd: (pin: FlowPin) => void;
  onCancel: () => void;
}

const OFF = "__off__";

function PinPicker({ members, dates, stations, onAdd, onCancel }: PinPickerProps) {
  const [member, setMember] = useState("");
  const [day, setDay] = useState(dates[0] ?? "");
  const [station, setStation] = useState(OFF);
  const st = stations.find((s) => s.name === station);
  const sessions = st && !st.on_call ? (st.sessions?.length ? st.sessions : ["AM", "PM"]) : [];
  const [session, setSession] = useState("");
  const sess = sessions.includes(session) ? session : (sessions[0] ?? "");
  const name = members.find((m) => m.id === member)?.display_name ?? "";
  const select = "rounded-sm border border-border bg-bg px-1 py-0.5 text-[11px]";
  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-sm border border-border bg-bg-subtle p-2"
      data-testid="fix-resolve-picker"
    >
      <select
        aria-label="Member"
        className={select}
        data-testid="fix-resolve-picker-member"
        value={member}
        onChange={(e) => setMember(e.target.value)}
      >
        <option value="">Member</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.display_name}
          </option>
        ))}
      </select>
      <select
        aria-label="Date"
        className={select}
        data-testid="fix-resolve-picker-date"
        value={day}
        onChange={(e) => setDay(e.target.value)}
      >
        {dates.map((d) => (
          <option key={d} value={d}>
            {reviewDate(d)}
          </option>
        ))}
      </select>
      <select
        aria-label="Station"
        className={select}
        data-testid="fix-resolve-picker-station"
        value={station}
        onChange={(e) => setStation(e.target.value)}
      >
        <option value={OFF}>Off</option>
        {stations.map((s) => (
          <option key={s.name} value={s.name}>
            {s.name}
          </option>
        ))}
      </select>
      {sessions.length > 0 ? (
        <select
          aria-label="Session"
          className={select}
          data-testid="fix-resolve-picker-session"
          value={sess}
          onChange={(e) => setSession(e.target.value)}
        >
          {sessions.map((s) => (
            <option key={s} value={s}>
              {s === "FULL_DAY" ? "Full day" : s}
            </option>
          ))}
        </select>
      ) : null}
      <Button
        type="button"
        size="sm"
        className="h-7 px-2"
        data-testid="fix-resolve-picker-add"
        disabled={!member || !day}
        onClick={() =>
          onAdd(
            station === OFF
              ? { membership_id: member, display_name: name, date: day, off: true }
              : {
                  membership_id: member,
                  display_name: name,
                  date: day,
                  off: false,
                  station,
                  session: sess || null,
                },
          )
        }
      >
        Add
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

export default FixAndResolvePanel;
